'use client';

import {
  Box,
  Text,
  Card,
  Flex,
  IconButton,
  Button as RadixButton,
} from '@radix-ui/themes';
import { useForm } from 'react-hook-form';
import {
  trpcConsumerSubmitUpsellItems,
  trpcConsumerListUpsellItemsOpen,
  trpcConsumerGetUpsellItemsInCart,
} from '@trashlab/api/src/generated/hooks';
import { TLSiteCartData } from '@trashlab/api/src/domains/consumer/types';
import { zodResolver } from '@hookform/resolvers/zod';
import { ShopStepFrame } from '@trashlab/sitesapp/src/organizations/fasttrac/components/shop/shop-step-frame';
import { IconPlus, IconMinus } from '@tabler/icons-react';
import { submitUpsellItemsSchema } from '@trashlab/api/src/domains/consumer/validations/upsell-item';
import currency from 'currency.js';
import { Image } from '@trashlab/sitesapp/src/organizations/fasttrac/components/shared/image';

export const SelectUpsellItemsStepSummary = ({
  cartData,
}: {
  cartData: TLSiteCartData;
}) => {
  const { data: upsellItemsCart } = trpcConsumerGetUpsellItemsInCart.useQuery({
    cartId: cartData.cart.id,
    tenantId: cartData.cart.tenantId,
  });

  if (!upsellItemsCart || upsellItemsCart.length === 0) {
    return null;
  }

  return (
    <>
      {upsellItemsCart.map((item) => (
        <Card key={item.upsellItemId} className="shadow-md p-2 mb-2">
          <Flex gap="4" align="center" wrap="wrap">
            {item.upsellItem.photo?.url && (
              <Box className="w-[100px] rounded-md overflow-hidden">
                <Image
                  alt={item.upsellItem.name}
                  src={item.upsellItem.photo.url}
                  className="object-contain"
                  width={'100%'}
                  height={'100%'}
                />
              </Box>
            )}
            <Flex direction="column" className="flex-1 min-w-[150px]">
              <Text size="2">{item.upsellItem.name}</Text>
              <Text size="2" weight="medium">
                {currency(item.appliedPrice ?? 0, {
                  fromCents: true,
                }).format()}
              </Text>
            </Flex>
            <Text size="1" color="gray">
              x{item.quantity}
            </Text>
          </Flex>
        </Card>
      ))}
    </>
  );
};

export const SelectUpsellItemsStep = ({ cartData }: { cartData: TLSiteCartData }) => {
  const { data: upsellItems = [], isLoading: upsellQueryLoading } =
    trpcConsumerListUpsellItemsOpen.useQuery({
      activeOnly: true,
      tenantId: cartData.cart.tenantId,
    });

  const { data: currentUpsellItems = [] } =
    trpcConsumerGetUpsellItemsInCart.useQuery({
      cartId: cartData.cart.id,
      tenantId: cartData.cart.tenantId,
    });

  const submitMutation = trpcConsumerSubmitUpsellItems.useMutation();

  const form = useForm({
    defaultValues: {
      items:
        currentUpsellItems?.map((item) => ({
          upsellItemId: item.upsellItemId,
          quantity: item.quantity,
        })) || [],
      isWebsite: true,
    },
    resolver: zodResolver(submitUpsellItemsSchema),
  });

  const selectedItems = form.watch('items');

  const handleToggleItem = (itemId: string) => {
    const items = form.getValues('items');
    const existingIndex = items.findIndex((x) => x.upsellItemId === itemId);

    if (existingIndex === -1) {
      form.setValue('items', [...items, { upsellItemId: itemId, quantity: 1 }]);
    } else {
      form.setValue(
        'items',
        items.filter((_, i) => i !== existingIndex),
      );
    }
  };

  const handleQuantityChange = (itemId: string, quantity: number) => {
    const items = form.getValues('items');
    const existingIndex = items.findIndex((x) => x.upsellItemId === itemId);

    if (existingIndex !== -1) {
      const updatedItems = [...items];
      updatedItems[existingIndex].quantity = quantity;
      form.setValue('items', updatedItems);
    }
  };

  const isItemSelected = (itemId: string) => {
    return selectedItems.some((item) => item.upsellItemId === itemId);
  };

  const getItemQuantity = (itemId: string) => {
    const item = selectedItems.find((item) => item.upsellItemId === itemId);
    return item ? item.quantity : 1;
  };

  if (upsellQueryLoading) {
    return (
      <Flex justify="center" p="4">
        <Text>Loading available items...</Text>
      </Flex>
    );
  }

  return (
    <ShopStepFrame
      cartId={cartData.cart.id}
      onNext={async () => {
        await submitMutation.mutateAsync({
          cartId: cartData.cart.id,
          tenantId: cartData.cart.tenantId,
          items: form.getValues('items'),
          isWebsite: true,
        });
      }}
      canContinue={true}
      isLoading={submitMutation.isLoading}
    >
      <Flex direction="column" gap="2" my="2">
        <Text size="4" weight="bold">
          Enhance your rental
        </Text>

        {upsellItems.map((item) => {
          const selected = isItemSelected(item.id);
          const quantity = getItemQuantity(item.id);

          return (
            <Card
              key={item.id}
              id={`upsell-item-${item.id}`}
              className={`
                shadow-sm p-4 m-2 cursor-pointer 
                transition-all duration-100 
                hover:shadow-md hover:transform hover:scale-[1.01]
                ${
                  selected
                    ? 'border-2 border-[var(--primary-color)]'
                    : 'border border-gray-100'
                }
              `}
              onClick={() => handleToggleItem(item.id)}
            >
              <Flex gap="3" align="center">
                <Box className="flex-shrink-0">
                  <Flex
                    justify="center"
                    align="center"
                    id={`upsell-checkbox-${item.id}`}
                    className={`
                      w-6 h-6 rounded-md border 
                      ${
                        selected
                          ? 'bg-[var(--primary-color)] border-[var(--primary-color)]'
                          : 'border-gray-300'
                      }
                    `}
                    onClick={(e) => {
                      e.stopPropagation();
                      handleToggleItem(item.id);
                    }}
                  >
                    {selected && (
                      <Box className="text-white">
                        <svg
                          width="14"
                          height="14"
                          viewBox="0 0 24 24"
                          fill="none"
                          xmlns="http://www.w3.org/2000/svg"
                        >
                          <path
                            d="M5 12L10 17L19 8"
                            stroke="currentColor"
                            strokeWidth="2.5"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                          />
                        </svg>
                      </Box>
                    )}
                  </Flex>
                </Box>

                <Box className="w-20 h-20 overflow-hidden rounded-md flex items-center justify-center bg-white flex-shrink-0">
                  {item.photo?.url ? (
                    <Image
                      alt={item.name}
                      src={item.photo.url}
                      className="object-contain"
                      width={80}
                      height={80}
                    />
                  ) : (
                    <Text color="gray" size="2">
                      No image
                    </Text>
                  )}
                </Box>

                <Flex direction="column" className="flex-1 min-w-0">
                  <Flex justify="between" align="start" gap="2">
                    <Box className="flex-1">
                      <Text weight="medium" size="3" className="mb-1">
                        {item.name}
                      </Text>
                      {item.description && (
                        <Text size="2" color="gray" className="line-clamp-2">
                          {item.description}
                        </Text>
                      )}
                    </Box>
                    <Flex
                      direction="column"
                      align="end"
                      className="flex-shrink-0"
                    >
                      <Text weight="bold" size="4">
                        {currency(item.price.chargeFee ?? 0, {
                          fromCents: true,
                        }).format()}
                      </Text>
                      <Text
                        size="2"
                        className={`mt-1 ${
                          selected
                            ? 'text-[var(--primary-color)]'
                            : 'text-gray-500'
                        }`}
                      >
                        {selected ? 'Added to order' : 'Add to order'}
                      </Text>
                    </Flex>
                  </Flex>

                  {selected && (
                    <Flex
                      align="center"
                      gap="2"
                      className="mt-3"
                      onClick={(e) => e.stopPropagation()}
                    >
                      <Text size="2" className="mr-1">
                        Quantity ({item.maxQuantityPerCart} available):
                      </Text>
                      <Flex align="center" gap="1">
                        <IconButton
                          size="1"
                          variant="soft"
                          id={`upsell-decrease-${item.id}`}
                          disabled={quantity <= 1}
                          onClick={() =>
                            handleQuantityChange(
                              item.id,
                              Math.max(1, quantity - 1),
                            )
                          }
                        >
                          <IconMinus size={14} />
                        </IconButton>
                        <RadixButton
                          size="1"
                          variant="surface"
                          disabled
                          id={`upsell-quantity-${item.id}`}
                        >
                          {quantity}
                        </RadixButton>
                        <IconButton
                          size="1"
                          variant="soft"
                          id={`upsell-increase-${item.id}`}
                          disabled={quantity >= (item.maxQuantityPerCart || 99)}
                          onClick={() =>
                            handleQuantityChange(
                              item.id,
                              Math.min(
                                item.maxQuantityPerCart || 99,
                                quantity + 1,
                              ),
                            )
                          }
                        >
                          <IconPlus size={14} />
                        </IconButton>
                      </Flex>
                    </Flex>
                  )}
                </Flex>
              </Flex>
            </Card>
          );
        })}

        {upsellItems.length === 0 && (
          <Card className="text-center py-6">
            <Text>No additional items available</Text>
          </Card>
        )}
      </Flex>
    </ShopStepFrame>
  );
};
